/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { WebSocketConnection } from '@app/core/services/websocket-connection.service';
import { ClusterId } from '@shared/types/cluster';
import {
  Cluster3xConfiguration,
  Cluster3xConnectionCredentials,
  Cluster3xMonitoringConfigurationRequestBody,
  Cluster3xNewUser,
  Cluster3xPhysicalTopology,
  Cluster3xTopology,
  Cluster3xUserLogin,
  Cluster3xUsers,
  ClusterConnectors,
  GetCluster3xConnectionCredentialsResponse,
  InitializeCluster3xRequestBody,
} from '@shared/types/cluster3x';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class Clusters3xApiService {
  private http = inject(HttpClient);
  private ws = inject(WebSocketConnection);

  getPhysicalTopology(clusterId: ClusterId): Observable<Cluster3xPhysicalTopology> {
    return this.http.get<Cluster3xPhysicalTopology>(`/api/v1/ignite3/cluster/${clusterId}/nodes/physical`);
  }

  initialize(clusterId: ClusterId, params: InitializeCluster3xRequestBody) {
    return this.http.post<null>(`/api/v1/ignite3/cluster/${clusterId}/init`, params);
  }

  clusterTopology(clusterId: string) {
    return this.ws.getTopic<Cluster3xTopology>(`/topic/cluster/gg9/topology/${clusterId}`);
  }

  getConnectionSettings(clusterId: string) {
    return this.http.get<GetCluster3xConnectionCredentialsResponse>(`/api/v1/ignite3/cluster/${clusterId}/connection`);
  }

  configureConnection(clusterId: string, credentials: Cluster3xConnectionCredentials) {
    return this.http.post<null>(`/api/v1/ignite3/cluster/${clusterId}/connection`, credentials);
  }

  getClusterConfiguration(clusterId: ClusterId) {
    return this.http.get<Cluster3xConfiguration>(`/api/v1/ignite3/cluster/${clusterId}/configuration`);
  }

  updateClusterConfiguration(clusterId: ClusterId, configuration: Partial<Cluster3xConfiguration>) {
    return this.http.patch<void>(`/api/v1/ignite3/cluster/${clusterId}/configuration`, configuration);
  }

  updateClusterMonitoringConfiguration(
    clusterId: ClusterId,
    configuration: Cluster3xMonitoringConfigurationRequestBody,
  ) {
    return this.http.patch<void>(`/api/v1/ignite3/cluster/${clusterId}`, configuration);
  }

  clusterConnectors() {
    return this.ws.getTopic<ClusterConnectors>(`/user/topic/connector/list`);
  }

  // Users

  getUsers(clusterId: ClusterId) {
    return this.http.get<Cluster3xUsers>(`/api/v1/ignite3/cluster/${clusterId}/users`);
  }

  addUser(clusterId: ClusterId, user: Cluster3xNewUser) {
    return this.http.post<null>(`/api/v1/ignite3/cluster/${clusterId}/users`, user);
  }

  removeUser(clusterId: ClusterId, login: Cluster3xUserLogin) {
    return this.http.delete<null>(`/api/v1/ignite3/cluster/${clusterId}/users/${login}`);
  }
}
